import { useEffect, useState, useCallback, useRef } from "react";
import {
	Alert,
	Box,
	Card,
	IconButton,
	Tooltip,
	Fade,
	Typography,
} from "@mui/material";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import * as hooks from "lib/hooks";

import { useSelector } from "react-redux";
import { RootState, SelectContainmentById } from "store";

import * as opt from "lib/opt";

import {
	ContainmentField,
	Dropdown,
	LoadingButton,
	PartSerialStatusTable,
} from "components";
import { TotalsSummaryCard } from "components/TotalsSummaryCard";
import { ContainmentSummary } from "components/ContainmentSummary";
import { FlexNetPartNumberWarning } from "components/FlexNetPartNumberWarning";
import { Containment, PlantsTableObj, StatusCell } from "models";
import { PlantStatusByContainmentResponse } from "lib/api";
import {
	ToFlexNetPartNumberGatePayload,
	ToUnsupportedFlexNetPlants,
	UnsupportedFlexNetPlant,
} from "lib/flexnet/partNumberGate";
import { AsrsStatuses, GetStatusEntriesForHoldSystem } from "enums/partSerialStatus";
import { ContainmentCardStyle } from "lib/styles/card";
import * as constants from "lib/util/constants";
import { ContainmentTypeConfig } from "pages/Upload/hooks/containmentTypeConfig";
import * as sort from "enums/sort";
import { Result } from "lib/result";
import { keyframes } from "@emotion/react";
import moment from "moment";

const pulse = keyframes`
	0% { opacity: 0.6; }
	50% { opacity: 1; }
	100% { opacity: 0.6; }
`;

const getCombinedId = (containmentId: string, plantId: string) =>
	`${containmentId}-${plantId}`;

export function ContainmentDetails({
	id,
	hideDownload = false,
	showFlexNetPartNumberGate = false,
}: {
	id: string;
	hideDownload?: boolean;
	/** When true (Add Serials), show FN version gate warning at bottom of Plant Status. */
	showFlexNetPartNumberGate?: boolean;
}): React.JSX.Element | null {
	const materialColors = hooks.UseMaterialColors();
	const api = hooks.UseMccApi();
	const { f: form } = hooks.UseFormContext();

	const containmentFromStore = useSelector((s: RootState) =>
		SelectContainmentById(s, id)
	);
	const [containment, setContainment] =
		useState<opt.Opt<Containment>>(containmentFromStore);

	useEffect(() => {
		if (containmentFromStore.IsNone()) {
			api.ContainmentById(id)
				.then((optContainment) => {
					setContainment(optContainment);
				})
				.catch(() => {
					setContainment(opt.None());
				})
				.catch(() => {
					setContainment(opt.None());
				});
		}
	}, [id]);

	const [plantData, setPlantData] = useState<
		PlantStatusByContainmentResponse<PlantsTableObj>
	>({
		containmentSerialCount: 0,
		plants: [],
		totalSerialsUploaded: 0,
	});
	const [order, setOrder] = useState(sort.Asc("name"));
	const [loading, setLoading] = useState(false);

	const typeConfig = containment
		.Map((c) => c.TypeConfig)
		.UnwrapOr(undefined);

	const [flexNetGateUnsupportedPlants, setFlexNetGateUnsupportedPlants] =
		useState<UnsupportedFlexNetPlant[]>([]);

	// Add-serials: evaluate impacted plants via server gate (SITMP-7497 / SITMP-7559).
	useEffect(() => {
		if (!showFlexNetPartNumberGate) {
			setFlexNetGateUnsupportedPlants([]);
			return;
		}
		if (containment.IsNone()) {
			setFlexNetGateUnsupportedPlants([]);
			form.setValue("partNumberRequired", null);
			return;
		}
		const c = containment.Unwrap();
		if (!c.TypeConfig?.hasHolds) {
			setFlexNetGateUnsupportedPlants([]);
			form.setValue("partNumberRequired", null);
			return;
		}
		const plants = (c.Plants || []).map((p: any) =>
			ToFlexNetPartNumberGatePayload({
				id: p.id,
				name: p.name,
				code: p.code,
				flexnetVersion: p.flexnetVersion ?? p.flexNetVersion,
			})
		);
		if (plants.length === 0) {
			setFlexNetGateUnsupportedPlants([]);
			form.setValue("partNumberRequired", null);
			return;
		}
		let cancelled = false;
		api.EvaluateFlexNetPartNumberGate(plants)
			.then((unsupported) => {
				if (cancelled) return;
				const unsupportedPlants = ToUnsupportedFlexNetPlants(unsupported);
				setFlexNetGateUnsupportedPlants(unsupportedPlants);
				// Drive frontend upload column layout from the gate.
				form.setValue(
					"partNumberRequired",
					unsupportedPlants.length > 0
				);
			})
			.catch(() => {
				// Fail closed: keep PN required if the gate API is unavailable.
				if (!cancelled) {
					setFlexNetGateUnsupportedPlants([]);
					form.setValue("partNumberRequired", true);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [showFlexNetPartNumberGate, containment, api, form]);

	const fetchPlantStatus = useCallback(() => {
		if (containment.IsSome()) {
			setLoading(true);

			api.PlantStatusByContainment(containment.Unwrap().id)
				.then((data) => {
					const entries = GetStatusEntriesForHoldSystem(
						typeConfig?.holdSystemName ?? "Flexnet"
					);

					const holdSystem = data.holdSystems?.[0];
					const rawPlants = holdSystem?.plants ?? data.plants;
					const sortedPlants = rawPlants
						.Map((p) => p.ToTableObj(entries))
						.Sort((a, b) => {
							if (order.Data === "name") {
								return order.Tag === "Asc"
									? a.name.localeCompare(b.name)
									: b.name.localeCompare(a.name);
							}
							const aCount = (a[order.Data] as StatusCell | undefined)?.count ?? 0;
							const bCount = (b[order.Data] as StatusCell | undefined)?.count ?? 0;
							return order.Tag === "Asc"
								? aCount - bCount
								: bCount - aCount;
						});

					setPlantData({
						containmentSerialCount: data.containmentSerialCount,
						totalSerialsUploaded: data.totalSerialsUploaded,
						plants: sortedPlants,
					});
				})
				.catch((err) => {
					console.error("Error fetching plant status:", err);
					setPlantData({
						containmentSerialCount: 0,
						plants: [],
						totalSerialsUploaded: 0,
					});
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [containment, order, typeConfig]);

	useEffect(() => {
		fetchPlantStatus();
	}, [fetchPlantStatus]);

	const handleSort = useCallback(
		(property: string) => {
			const isAsc = order.Tag === "Asc" && property === order.Data;
			setOrder(isAsc ? sort.Desc(property) : sort.Asc(property));
		},
		[order]
	);

	const [selectedPlant, setSelectedPlant] = useState<opt.Opt<PlantsTableObj>>(
		opt.None()
	);
	const [downloading, setDownloading] = useState(false);
	const [countdown, setCountdown] = useState<Record<string, number>>({});

	const countdownIntervals = useRef<Record<string, NodeJS.Timeout>>({});
	const snack = hooks.UseSnackBar();

	const [unmappedFiles, setUnmappedFiles] = useState<string[]>([]);
	const [unmappedDownloading, setUnmappedDownloading] = useState<string | null>(null);

	useEffect(() => {
		if (!containment.Map((c) => c.TypeConfig?.hasUnmappedSerials ?? false).UnwrapOr(false)) return;
		api.UnmappedFiles(id)
			.then(setUnmappedFiles)
			.catch(() => setUnmappedFiles([]));
	}, [id, typeConfig]);

	const handleUnmappedDownload = async (fileName: string) => {
		setUnmappedDownloading(fileName);
		try {
			const blob = await api.DownloadFile(fileName);
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = fileName;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			snack.open(Result.Err("Download failed — the file may have expired."));
		} finally {
			setUnmappedDownloading(null);
		}
	};

	const handleDownload = async () => {
		const plant = selectedPlant.Expect("Plant should be selected");
		const combinedId = getCombinedId(id, plant.id);

		setDownloading(true);
		try {
		const holdSystemId = typeConfig?.holdSystemId ?? constants.FlexnetHoldSystemId;
			const resp = await api.ContainmentDataDownload(
				id,
				holdSystemId,
				plant.id
			);
			snack.open(Result.Ok(resp.message));
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Unknown error";
			snack.open(Result.Err(message));
		} finally {
			setDownloading(false);
		}

		if (countdownIntervals.current[combinedId]) {
			clearInterval(countdownIntervals.current[combinedId]);
		}

		let ms = 60000;
		setCountdown((prev) => ({ ...prev, [combinedId]: ms }));

		const interval = setInterval(() => {
			ms -= 1000;
			if (ms <= 0) {
				clearInterval(interval);
				delete countdownIntervals.current[combinedId];
				setCountdown((prev) => ({ ...prev, [combinedId]: 0 }));
			} else {
				setCountdown((prev) => ({ ...prev, [combinedId]: ms }));
			}
		}, 1000);

		countdownIntervals.current[combinedId] = interval;
	};

	const formatTimeRemaining = (ms: number): string => {
		if (!ms || ms <= 0) return "0:00";
		const seconds = Math.floor(ms / 1000);
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}:${remainingSeconds.String().PadStart(2, "0")}`;
	};

	const currentPlantMsRemaining = selectedPlant
		.Map((p) => countdown[getCombinedId(id, p.id)])
		.Filter((ms) => ms > 0);

	const buttonLabel = currentPlantMsRemaining
		.Map((ms) => `Available in ${formatTimeRemaining(ms)}`)
		.UnwrapOr("Download Data");

	return (
		<Box>
			{containment
				.Map((c) => {
					return (
						<Card key={c.id} sx={ContainmentCardStyle()}>
							<ContainmentSummary
								mccId={c.friendlyId}
								name={c.name}
								description={c.description}
								containmentTypeLabel={c.SourceSystem || undefined}
								typeConfig={c.TypeConfig}
								initialHoldName={c.initial?.name}
								subsequentHoldName={c.subsequent?.name}
								showSectorsAndPlants={false}
								showPartNumbers={false}
							/>
						</Card>
					);
				})
				.UnwrapOrNil()}

			{/* ASRS override alerts */}
			{(() => {
			if (!typeConfig?.hasOverrideAlerts) return null;
				const pulledTotal = plantData.plants.reduce(
					(sum, p) => sum + ((p[AsrsStatuses.Pulled.id] as StatusCell)?.count ?? 0), 0
				);
				const viadatUnlockedTotal = plantData.plants.reduce(
					(sum, p) => sum + ((p[AsrsStatuses["Viadat Unlocked"].id] as StatusCell)?.count ?? 0), 0
				);
				return (
					<>
						{pulledTotal > 0 && (
							<Alert
								severity="warning"
								data-test-selector="alert-override-detected"
								sx={{ mb: 2, alignItems: "center" }}
							>
								Override Detected: {pulledTotal} LPN Pulled in ASRS while still Locked in MCC
							</Alert>
						)}
						{viadatUnlockedTotal > 0 && (
							<Alert
								severity="warning"
								data-test-selector="alert-viadat-unlocked"
								sx={{ mb: 2, alignItems: "center" }}
							>
								Viadat Unlock Detected: {viadatUnlockedTotal} LPN Unlocked in ASRS while still Locked in MCC
							</Alert>
						)}
					</>
				);
			})()}

			{/* Part serial / LPN status table */}
			<Card sx={ContainmentCardStyle()}>
				<PartSerialStatusTable
					plants={plantData.plants}
					order={order}
					loading={loading}
					onSort={handleSort}
					statusEntries={GetStatusEntriesForHoldSystem(
						typeConfig?.holdSystemName ?? "Flexnet"
					)}
					title={typeConfig?.statusTableTitle ?? constants.PlantStatusFlexnetTitle}
					headerAction={
						<Tooltip
							data-test-selector="refresh-plant-status"
							title={`Refresh ${typeConfig?.itemLabel ?? "Part Serial"} Statuses`}
							arrow
							placement="top"
						>
							<IconButton
								data-test-selector="refresh-plant-status-button"
								onClick={fetchPlantStatus}
								size="small"
							>
								<CloudSyncIcon color="primary" />
							</IconButton>
						</Tooltip>
					}
				/>
				{flexNetGateUnsupportedPlants.length > 0 && (
					<Box sx={{ mt: 2 }}>
						<FlexNetPartNumberWarning
							unsupportedPlants={flexNetGateUnsupportedPlants}
						/>
					</Box>
				)}
			</Card>

			{/* Total Serials/LPN Summary Section */}
			{plantData.plants.length > 0 && (
				<TotalsSummaryCard
					totalCount={plantData.containmentSerialCount}
					totalUploaded={plantData.totalSerialsUploaded}
					itemLabel={typeConfig?.itemLabel ?? "Serial"}
				/>
			)}

			{/* Download section — Flexnet plants only */}
			{plantData.plants.length > 0 && !hideDownload && (
				<Card sx={{ ...ContainmentCardStyle(), mt: 3 }}>
					<Box display="flex" flexDirection="column" gap={1.5}>
						<Typography
							sx={{
								fontWeight: 600,
								fontSize: "0.9rem",
							}}
						>
							Download Data for Plant
						</Typography>
						<Box
							display="flex"
							flexWrap="wrap"
							gap={2}
							alignItems="center"
							sx={{ minWidth: 0 }}
						>
							<Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
								<Dropdown
									data={plantData.plants}
									value={selectedPlant.UnwrapOrNil()}
									getOptionLabel={(p) => p.name}
									onChange={(val) => {
										if (val) {
											setSelectedPlant(opt.Some(val));
										} else {
											setSelectedPlant(opt.None());
										}
									}}
									fullWidth
									multiple={false}
									label="Plant"
									hideLabel
									textFieldProps={{ placeholder: "Select Plant" }}
								/>
							</Box>
							<Fade in={selectedPlant.IsSome()} timeout={300}>
								<Tooltip title={buttonLabel} placement="top">
									<LoadingButton
										loading={downloading}
										onClick={handleDownload}
										disabled={currentPlantMsRemaining.IsSome()}
										variant={
											materialColors.isDark
												? "outlined"
												: "contained"
										}
										color="primary"
										size="medium"
										sx={{
											height: 43,
											minWidth: 0,
											maxWidth: "100%",
											overflow: "hidden",
											"& .MuiButton-label": {
												overflow: "hidden",
												textOverflow: "ellipsis",
											},
										}}
										startIcon={
											currentPlantMsRemaining.IsSome() ? (
												<AccessTimeIcon
													fontSize="small"
													sx={{
														animation: `${pulse} 1.5s infinite`,
													}}
												/>
											) : (
												<CloudDownloadIcon fontSize="small" />
											)
										}
									>
									{buttonLabel}
								</LoadingButton>
							</Tooltip>
							</Fade>
						</Box>
					</Box>
				</Card>
			)}
			{unmappedFiles.length > 0 && (
				<Card sx={{ ...ContainmentCardStyle(), mt: 3 }}>
					<Box display="flex" flexDirection="column" gap={1.5}>
						<Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
							Unmapped Serials
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Serials that could not be mapped to a License Plate Number (LPN).
						</Typography>
						{unmappedFiles.map((fileName) => {
							const displayName = fileName.replace(/\.csv$/, "");
							return (
								<Box
									key={fileName}
									display="flex"
									alignItems="center"
									gap={1}
								>
									<DescriptionOutlinedIcon
										fontSize="small"
										color="action"
									/>
									<LoadingButton
										loading={unmappedDownloading === fileName}
										onClick={() => handleUnmappedDownload(fileName)}
										variant="text"
										size="small"
										sx={{
											textTransform: "none",
											justifyContent: "flex-start",
											minWidth: 0,
										}}
									>
										{displayName}
									</LoadingButton>
								</Box>
							);
						})}
					</Box>
				</Card>
			)}
			{containment
				.Map((c) => (
					<Card key={c.id} sx={ContainmentCardStyle()}>
						<Box
							data-test-selector="containment-details-info"
							display="flex"
							flexDirection="column"
							gap={1.5}
						>							
							<ContainmentField
							title="Last Updated By"
							value={(c as any)?.updateUser?.name || "—"}
							/>
							<ContainmentField
							title="Last Updated Date"
							value={
								c.updateDate
								? moment.utc(c.updateDate).local().format("DD-MMM-YY HH:mm:ss")
								: "—"
							}
							/>
						</Box>
						
					</Card>
				))
				.UnwrapOrNil()}
		</Box>
	);
}

function useGetTimeRemaining(containmentId: string) {
	const timerState = useSelector(
		(state: RootState) => state.downloadDisabledTimers
	);

	return useCallback(
		(plantId: string): opt.Opt<number> => {
			const combinedId = getCombinedId(containmentId, plantId);
			return timerState.Get(combinedId);
		},
		[timerState]
	);
}
